/**
 * Post-login routing (issue #25).
 *
 * Identity here is a MEMBERSHIP — (user × tenant × role) — so "where do I land
 * after signing in" is a question about the selected role, not about the human.
 * The same person signs in as a learner at their school and as an admin at the
 * practice they own, and lands in a different app each time.
 *
 * The mapping lives in this package rather than in either app because both
 * apps need it: the learner app routes people OUT of itself after login, and
 * the admin app will need the same table to bounce someone who arrived in the
 * wrong place. Keeping one copy is what stops the two drifting.
 *
 * `Record<Role, Destination>` is the guard: adding a role to `@cusp/core`'s
 * ROLES without deciding where it lands is a typecheck failure, not a runtime
 * fall-through to the learner app.
 */
import type { Role } from '@cusp/core';
import type { Identity } from './identity.js';

/** The three front ends the platform has (or will have). */
export type Surface = 'learner' | 'admin' | 'console';

export interface Destination {
  surface: Surface;
  /**
   * An ABSOLUTE path, not a router route. The deployed proxy path-routes ONE
   * hostname across three containers (`/admin/*` → admin, everything else →
   * web), so a hop between surfaces is a real navigation and a router `Link`
   * could never express it. Within a surface the app uses its own router.
   */
  href: string;
  /**
   * False while the surface does not exist yet. The platform console is #26;
   * until it ships, a `cusp_admin` must be told that plainly rather than
   * redirected into a 404 — the greyed-out-surface rule applied to routing.
   */
  built: boolean;
  /** What to call the destination in a button or link, so both apps agree. */
  label: string;
}

const DESTINATIONS: Record<Role, Destination> = {
  learner: { surface: 'learner', href: '/courses', built: true, label: 'Your courses' },
  faculty: { surface: 'admin', href: '/admin/', built: true, label: 'Institution admin' },
  institution_admin: {
    surface: 'admin',
    href: '/admin/',
    built: true,
    label: 'Institution admin',
  },
  /**
   * Built by #26 — but reached differently from the other two: the console
   * authenticates by Google Workspace SSO plus a platform_admins grant, not by
   * membership. The `cusp_admin` role row is retired from active use (D11);
   * this mapping remains so any legacy membership still lands somewhere
   * truthful — the console's own sign-in.
   */
  cusp_admin: { surface: 'console', href: '/console/', built: true, label: 'Platform console' },
};

/** Where a membership with this role belongs. */
export function destinationForRole(role: Role): Destination {
  return DESTINATIONS[role];
}

/** Convenience over the identity store; null identity has no destination. */
export function destinationForIdentity(identity: Identity | null): Destination | null {
  return identity === null ? null : destinationForRole(identity.role);
}

/**
 * Leave this SPA for another app on the same origin.
 *
 * Wrapped rather than called inline so screens never reach for `window`
 * directly — which also gives jsdom suites a seam to observe the hop, since
 * jsdom refuses to navigate and would otherwise only log "not implemented".
 */
export function leaveForApp(href: string): void {
  window.location.assign(href);
}
